import { gsap } from "gsap";
import { DisplayObject, IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { GameFont } from "../../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { LoginResourcePackage } from "../../resources/LoginResourcePackage";
import { InputType } from "../../InputType";
import { LoginHtmlInput } from "by/gamefactory/cityversetycoon/scenes/common/components/LoginHtmlInput";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { TextWithUnderline } from "by/gamefactory/cityversetycoon/scenes/game/components/TextWithUnderline";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { IFocusable } from "by/gamefactory/cityversetycoon/scenes/layers/IFocusable";
import { AsyncOperationNotification } from "../../../../common/components/AsyncOperationNotification";
import { StringFormatExtention } from "../../../../../../extensions/StringFormatExtention";
import { VirtualScreenEvent } from "../../../../../../analytics/events/VirtualScreenEvent";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IEmailVerificationPopupController } from "../controller/IEmailVerificationPopupController";
import { FullscreenInputBlocker } from "by/gamefactory/cityversetycoon/scenes/game/components/FullscreenInputBlocker";

export class EmailVerificationPopupView extends BaseScalableView<boolean> implements IFocusable {
    public readonly focusable: boolean = true;

    private static readonly HEADER_TEXT = "EMAIL VERIFICATION CODE";
    private static readonly DESCRIPTION_TEXT = "Please enter the verification code\nsent to your email address:";
    private static readonly DIDNT_RECEIVE_TEXT = "didn't receive a verification code?";
    private static readonly CLICK_HERE_TEXT = "CLICK HERE TO SEND IT AGAIN";
    private static readonly CODE_WAS_SEND_TEXT = "Verification Code was sent\nto your email address";
    private static readonly RESET_BUTTON_TEXT = "CONTINUE";
    private static readonly DONT_KNOW_TEXT = "Don't know how to access\nto this email, please";
    private static readonly CONTACT_US_TEXT = "CONTACT US";
    private static readonly FOOTER_TEXT = "WE WILL CREATE AN IN-GAME WALLET\nFOR YOU BASED ON YOUR EMAIL"

    private readonly loginResources: LoginResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly controller: IEmailVerificationPopupController;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    private fadeScreen: FadeScreen;
    private backButton: ButtonWithScaleAnimation;
    private emailIcon: Sprite;
    private headerText: Text;
    private pleaseEnterText: Text;
    private mailNameText: Text;
    private didntReceiveText: Text;
    private clickHereText: Text;
    private verificationCodeText: Text;
    private continueButton: ButtonWithScaleAnimation;
    private dontKnowText: Text;
    private contactUsButton: TextWithUnderline;
    private emailCodeInput: LoginHtmlInput;
    private email: string;
    private footerText: Text;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        loginResources: LoginResourcePackage,
        commonResources: CommonResourcesPackage,
        controller: IEmailVerificationPopupController,
        canvasScaler: ICanvasScaler,
        analyticsProvider: IAnalyticProviderWrapper,
        asyncOperationNotification: AsyncOperationNotification,
        email: string,
        logger: ILogger
    ) {
        super(operationControls, canvasScaler);
        this.loginResources = loginResources;
        this.commonResources = commonResources;
        this.controller = controller;
        this.analyticsProvider = analyticsProvider;
        this.logger = logger;
        this.email = email;
        this.asyncOperationNotification = asyncOperationNotification;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public start(): void {
        this.show();
    }

    private build(): void {
        const width = this.canvasScaler.referenceWidth;
        const height = this.canvasScaler.referenceHeight;
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, width, height);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);

        this.backButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.backButton.onUp.add(this.backButtonClickHandle, this);
        this.addChild(this.backButton);

        this.emailIcon = new Sprite(this.loginResources.assets_views_login_login_failed_icons_email_icon_png);
        this.emailIcon.anchor.set(0.5, 0);
        this.addChild(this.emailIcon);

        this.headerText = new Text(EmailVerificationPopupView.HEADER_TEXT, new HeaderTextStyle());
        this.headerText.anchor.set(0.5, 0);
        this.addChild(this.headerText);

        this.pleaseEnterText = new Text(EmailVerificationPopupView.DESCRIPTION_TEXT, new DescriptionTextStyle());
        this.pleaseEnterText.anchor.set(0.5, 0);
        this.addChild(this.pleaseEnterText);

        this.mailNameText = new Text(StringFormatExtention.emailFormatWithStars(this.email), new MailNameTextStyle());
        this.mailNameText.anchor.set(0.5, 0);
        this.addChild(this.mailNameText);

        this.emailCodeInput = new LoginHtmlInput(this.loginResources, this.canvasScaler, InputType.EMAIL_CODE);
        this.addChild(this.emailCodeInput);

        this.didntReceiveText = new Text(EmailVerificationPopupView.DIDNT_RECEIVE_TEXT, new DescriptionTextStyle());
        this.didntReceiveText.anchor.set(0.5, 0);
        this.addChild(this.didntReceiveText);

        this.clickHereText = new TextWithUnderline(EmailVerificationPopupView.CLICK_HERE_TEXT, new ClickHereTextStyle(), 0.5, -5);
        this.clickHereText.anchor.set(0.5, 0);
        this.clickHereText.eventMode = 'static';
        this.clickHereText.on('pointerup', this.sendCodeAgainClickHandle, this);
        this.addChild(this.clickHereText);

        this.verificationCodeText = new Text(EmailVerificationPopupView.CODE_WAS_SEND_TEXT, new VerificationCodeTextStyle());
        this.verificationCodeText.anchor.set(0.5, 0);
        this.addChild(this.verificationCodeText);

        this.continueButton = new ButtonWithScaleAnimation(this.loginResources.assets_views_login_buttons_button_green_long_png);
        this.continueButton.onUp.add(this.continueButtonClickHandle, this);
        const resetButtonText = new Text(EmailVerificationPopupView.RESET_BUTTON_TEXT, new ButtonTextStyle());
        resetButtonText.position.set(this.continueButton.width * 0.5 - resetButtonText.width * 0.5, this.continueButton.height * 0.5 - resetButtonText.height * 0.5);
        this.continueButton.addChild(resetButtonText);
        this.addChild(this.continueButton);

        this.dontKnowText = new Text(EmailVerificationPopupView.DONT_KNOW_TEXT, new DescriptionTextStyle());
        this.dontKnowText.anchor.set(0.5, 0);
        this.addChild(this.dontKnowText);

        this.contactUsButton = new TextWithUnderline(EmailVerificationPopupView.CONTACT_US_TEXT, new ContactUsTextStyle(), 0.5);
        this.contactUsButton.eventMode = 'static';
        this.contactUsButton.on('pointerup', this.contactUsButtonClickHandle, this);
        this.contactUsButton.anchor.set(0.5, 0);
        this.addChild(this.contactUsButton);

        this.footerText = new Text(EmailVerificationPopupView.FOOTER_TEXT, new FooterTextStyle());
        this.addChild(this.footerText);
    }

    private async show(): Promise<void> {
        this.verificationCodeText.visible = false;
        this.verificationCodeText.alpha = 0;
        this.dontKnowText.visible = false;
        this.dontKnowText.alpha = 0;
        await this.showView();
    }

    private async showView(): Promise<void> {
        this.fadeScreen.fadeIn();
        this.emailCodeInput.hide();
        const showAnimation = new gsap.core.Timeline();
        showAnimation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.15, ease: "power1.in" });
        await showAnimation.play();
        showAnimation.kill();
        this.emailCodeInput.show();
    }

    private async backButtonClickHandle(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.hideView();
        this.operationControls.complete(false);
    }

    private async continueButtonClickHandle(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Code verification continue button click", this.analyticsProvider.currentEventIndex));
        try {
            const code = this.emailCodeInput.value;
            if (!this.emailCodeInput.isTextMatchingRegex()) {
                return;
            }
            this.asyncOperationNotification.show();
            this.emailCodeInput.hide();
            if (await this.controller.signWithCode(this.email, code)) {
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email sign in with code verification completed", this.analyticsProvider.currentEventIndex));
                this.asyncOperationNotification.hide();
                await this.hideView();
                this.operationControls.complete(true);
            }
            else {
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email sign in with code verification failed", this.analyticsProvider.currentEventIndex));
                this.asyncOperationNotification.hide();
                await this.hideView();
                await this.controller.showValidationFailedPopup();
                await this.showView();
                this.emailCodeInput.show();
            }
        } catch (error: unknown) {
            //TODO egorja: A lot of dublicates, needed common point for result of verefication in case of error and wrong code
            this.logger.debug(error instanceof Error ? error.message : String(error));
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email sign in with code verification failed", this.analyticsProvider.currentEventIndex));
            this.asyncOperationNotification.hide();
            await this.hideView();
            await this.controller.showValidationFailedPopup();
            await this.showView();
            this.emailCodeInput.show();
        } finally {
            this.removeChild(inputBlocker);
        }
    }

    private async sendCodeAgainClickHandle(): Promise<void> {
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email send code again button click", this.analyticsProvider.currentEventIndex));
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        try {
            await this.controller.resendCode(this.email);
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email send code again completed", this.analyticsProvider.currentEventIndex));
            await this.showVerificationCodeTextWithAnimation(!this.verificationCodeText.visible);
        } catch (error: unknown) {
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email send code again failed", this.analyticsProvider.currentEventIndex));
            this.logger.error(error);
        } finally {
            this.removeChild(inputBlocker);
        }
    }

    private contactUsButtonClickHandle(): void {
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Email send code contact us button click", this.analyticsProvider.currentEventIndex));
        this.controller.openIntercom(this.email);
    }

    private async hideView(): Promise<void> {
        this.emailCodeInput.hide();
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.15, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
    }

    private async showVerificationCodeTextWithAnimation(isFirst: boolean = false): Promise<void> {
        const topElements: Array<DisplayObject> = [
            this.emailIcon,
            this.headerText,
            this.pleaseEnterText,
            this.mailNameText,
            this.emailCodeInput,
            this.didntReceiveText,
            this.clickHereText
        ];
        const bottomElements: Array<DisplayObject> = [
            this.continueButton,
            this.contactUsButton
        ];
        let topDistance = -100;
        let bottomDistance = 30;
        if (this.dontKnowText.visible) {
            bottomElements.push(this.dontKnowText);
            topDistance = -65;
            bottomDistance = 65;
        }
        const showAnimation = new gsap.core.Timeline();
        if (isFirst) {
            topElements.forEach(element => {
                showAnimation.to(element, { y: element.position.y + topDistance, duration: 0.3, ease: "power1.out" }, "<");
            });
            bottomElements.forEach(element => {
                showAnimation.to(element, { y: element.position.y + bottomDistance, duration: 0.3, ease: "power1.out" }, "<");
            });
        }
        showAnimation.fromTo(this.verificationCodeText, { alpha: 0 }, { alpha: 1, duration: 0.3, onStart: () => this.verificationCodeText.visible = true, ease: "power1.out" }, "<50%");
        await showAnimation.play();
        showAnimation.kill();
    }

    private layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.backButton.position.set(width * 0.06 + this.backButton.width * 0.5, height * 0.13 + this.backButton.height * 0.5);
        this.emailIcon.position.set(width * 0.5, height * 0.267);
        this.headerText.position.set(width * 0.5, height * 0.368);
        this.pleaseEnterText.position.set(width * 0.5, height * 0.416);
        this.mailNameText.position.set(width * 0.5, this.pleaseEnterText.y + this.pleaseEnterText.height - 3);
        this.emailCodeInput.position.set(width * 0.5 - this.emailCodeInput.width * 0.5, height * 0.504);
        this.didntReceiveText.position.set(width * 0.5, height * 0.598);
        this.verificationCodeText.position.set(width * 0.5, height * 0.614);
        this.clickHereText.position.set(width * 0.5, this.didntReceiveText.y + this.didntReceiveText.height - 3);
        this.continueButton.position.set(width * 0.5 - this.continueButton.width * 0.5, height * 0.7 - this.continueButton.height * 0.5);
        this.dontKnowText.position.set(width * 0.5, height * 0.73);
        this.contactUsButton.position.set(width * 0.5, height * 0.75);
        this.footerText.position.set(201, height - this.footerText.height - 96);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.backButton.onUp.remove(this.backButtonClickHandle, this);
        this.continueButton.onUp.remove(this.continueButtonClickHandle, this);
        this.contactUsButton.off('pointerup', this.contactUsButtonClickHandle, this);
        this.clickHereText.off('pointerup', this.sendCodeAgainClickHandle, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48,
            lineHeight: 52.5
        }));
    }
}

class DescriptionTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT_REGULAR,
            fontSize: 40,
            lineHeight: 45,
            letterSpacing: 1.5
        }));
    }
}

class MailNameTextStyle extends DescriptionTextStyle {
    public constructor() {
        super();
        this.fontFamily = GameFont.DEFAULT_FONT;
    }
}

class ClickHereTextStyle extends MailNameTextStyle {
    public constructor() {
        super();
        this.letterSpacing = 0;
        this.fill = '#9DD55A';
    }
}

class VerificationCodeTextStyle extends DescriptionTextStyle {
    public constructor() {
        super();
        this.fill = '#9DD55A';
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.8,
            dropShadowBlur: 5,
            dropShadowDistance: 2,
            dropShadowAlpha: 0.5,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64,
            miterLimit: 0
        }));
    }
}

class ContactUsTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40,
        }));
    }
}

class FooterTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ADADAD",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36
        });
    }
}